import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import CutsList from './CutsList';
import PrimalCutsGraphic from './PrimalCutsGraphic';
import CutsHeader from './CutsHeader';
import CutsPromos from './CutsPromos';
import CutsResultsHeader from './CutsResultsHeader';
import { labelForSelection } from './primalRegions';
import { AJAX_URL } from '../../config/index.js';

export default function Cuts({ pageId }) {
  const [primalCuts, setPrimalCuts] = useState([]);
  const [cookingMethods, setCookingMethods] = useState([]);
  const [cutsList, setCutsList] = useState([]);
  const [promos, setPromos] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [cookingMethodFilter, setCookingMethodFilter] = useState('');
  const urlAppliedRef = useRef(false);

  // Load everything once. All filtering (primal, cooking method, search) runs
  // client-side against this list, so results are instant — no reload/skeleton.
  useEffect(() => {
    let isSubscribed = true;
    const url = `${AJAX_URL}post_wlQueryCuts`;
    const payload = new FormData();
    if (pageId) payload.append('pageId', pageId);

    axios
      .post(url, payload)
      .then((res) => {
        if (!isSubscribed) return;
        setPrimalCuts(res.data.data.primalCuts);
        setCookingMethods(res.data.data.cookingMethods);
        setCutsList(res.data.data.cuts);
        setPromos(res.data.data.promos || []);
      })
      .catch((err) => console.log(err.message));

    return () => (isSubscribed = false);
  }, []);

  // Apply URL params once the primal list has loaded: ?primal={slug} pre-selects
  // a primal (e.g. from a cut detail breadcrumb); ?search={term} pre-fills search.
  useEffect(() => {
    if (urlAppliedRef.current || primalCuts.length === 0) return;
    urlAppliedRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const primalSlug = params.get('primal');
    const searchParam = params.get('search');

    if (searchParam) setSearchTerm(searchParam);

    if (primalSlug) {
      setPrimalCuts((prev) =>
        prev.map((item) => ({ ...item, selected: item.slug === primalSlug }))
      );

      setTimeout(() => {
        const results = document.getElementById('cuts-results');
        if (results) results.scrollIntoView({ behavior: 'smooth' });
      }, 400);
    }
  }, [primalCuts]);

  const selectedPrimals = primalCuts.filter((item) => item.selected);
  const selectedPrimalIds = selectedPrimals.map((item) => Number(item.id));

  const trimmedSearch = searchTerm.trim().toLowerCase();
  const displayedCuts = cutsList.filter((cut) => {
    const matchesPrimal =
      selectedPrimalIds.length === 0 ||
      selectedPrimalIds.includes(Number(cut.primal_cut));
    const matchesSearch =
      !trimmedSearch || cut.name.toLowerCase().includes(trimmedSearch);
    const matchesCookingMethod =
      !cookingMethodFilter ||
      (Array.isArray(cut.cooking_methods) &&
        cut.cooking_methods.map(Number).includes(Number(cookingMethodFilter)));
    return matchesPrimal && matchesSearch && matchesCookingMethod;
  });

  // A region can select more than one primal (loin ⇢ Loin + Sirloin). Build the
  // results-header primal from the selection: the alias label + the display
  // primal's description/butcher's note.
  const headingLabel = labelForSelection(selectedPrimals);
  const displayPrimal =
    selectedPrimals.find(
      (item) => item.name.toLowerCase() === (headingLabel || '').toLowerCase()
    ) ||
    selectedPrimals[0] ||
    null;
  const resultsPrimal = selectedPrimals.length
    ? {
        name: headingLabel,
        description: displayPrimal ? displayPrimal.description : '',
        butchers_note: displayPrimal ? displayPrimal.butchers_note : ''
      }
    : null;

  const hasActiveSelection = selectedPrimals.length > 0 || trimmedSearch !== '';

  const clearFilters = () => {
    setPrimalCuts((prev) => prev.map((item) => ({ ...item, selected: false })));
    setSearchTerm('');
    setCookingMethodFilter('');
  };

  return (
    <>
      <CutsHeader searchTerm={searchTerm} setSearchTerm={setSearchTerm}>
        <PrimalCutsGraphic
          setPrimalCuts={setPrimalCuts}
          primalCuts={primalCuts}
        />
      </CutsHeader>

      {hasActiveSelection && (
        <div id="cuts-results">
          <CutsResultsHeader
            selectedPrimal={resultsPrimal}
            searchTerm={searchTerm}
            resultCount={displayedCuts.length}
            cookingMethods={cookingMethods}
            cookingMethodFilter={cookingMethodFilter}
            setCookingMethodFilter={setCookingMethodFilter}
            onClear={clearFilters}
          />
          {displayedCuts.length > 0 ? (
            <CutsList cutsList={displayedCuts} />
          ) : (
            <div style={{ textAlign: 'center', marginBottom: '50px' }}>
              No results found.
            </div>
          )}
        </div>
      )}

      {/* Promos are a persistent footer on the landing — shown below the
          results when a primal/search is active, and on their own otherwise. */}
      <CutsPromos promos={promos} />
    </>
  );
}
